/**
 * Live updates from the CRM backend (SignalR). NOT RUN against your app: nothing here was built or tested.
 *
 *   npm i @microsoft/signalr
 *
 * The server sends one message, "changed", for every record that is created, updated or deleted in ANY service of the signed-in
 * person's tenant (and for named actions such as "Advance stage"). It only sends entities the person may read ("<permission>.read").
 * The message says WHAT changed, not the new values: fetch the row again with crmClient (`crm.get`/`crm.list`) and put it in state.
 *
 * Address: the gateway, e.g. http://localhost:5100 (hub at /hubs/crm). The token is the same bearer token the REST calls use.
 * Permissions are read when the connection opens, so after a role change or a new token the connection must be reopened
 * (it is, automatically, when the token expires, because the server then closes it and this code reconnects with `getToken()`).
 */
import { HubConnectionBuilder, HubConnectionState, LogLevel, type HubConnection } from "@microsoft/signalr";

export interface CrmChange {
  eventId: string;
  service: string;            // "sales", "customers", ...
  entity: string;             // class name: "Lead", "Account", ...
  id: string;                 // GUID of the record
  number?: string | null;     // "LD-0001" (only when `detailed`)
  action: string;             // "Created" | "Updated" | "Deleted" | a named action such as "Advance stage"
  summary?: string | null;    // "Lead LD-0001 (Acme) updated" (only when `detailed`)
  by?: string | null;         // who did it (only when `detailed`)
  byId?: string | null;       // use it to skip your own echo: if byId === my user id, the screen is already current
  at: string;                 // ISO time
  detailed: boolean;          // false for entities without a permission of their own (for example order lines): only the fact is announced
}

export type RealtimeState = "connecting" | "connected" | "reconnecting" | "disconnected";

export interface RealtimeOptions {
  baseUrl: string;                                                   // gateway address, no trailing slash needed
  getToken: () => string | undefined | Promise<string | undefined>;  // called on every (re)connect
  onChange: (change: CrmChange) => void;
  onState?: (state: RealtimeState) => void;
  /** Called after a lost connection came back. Changes made in between are NOT replayed: reload what is on screen. */
  onResync?: () => void;
}

/** CRM.tsx list name for an entity class. Extend it when you map more entities in crmClient's ENTITIES. */
export const SOURCE_OF: Record<string, string> = {
  Account: "accounts", Contact: "contacts", Lead: "leads", Opportunity: "opportunities", Activity: "activities", Case: "cases",
};
export const sourceOf = (c: Pick<CrmChange, "entity">): string | undefined => SOURCE_OF[c.entity];

const RETRY_MS = [0, 2000, 5000, 10000, 30000];

export function connectCrmRealtime(o: RealtimeOptions): { stop: () => Promise<void>; connection: HubConnection } {
  const connection = new HubConnectionBuilder()
    .withUrl(`${o.baseUrl.replace(/\/+$/, "")}/hubs/crm`, { accessTokenFactory: async () => (await o.getToken()) ?? "" })
    // never give up: the default policy stops after four tries
    .withAutomaticReconnect({ nextRetryDelayInMilliseconds: (ctx) => RETRY_MS[Math.min(ctx.previousRetryCount, RETRY_MS.length - 1)] })
    .configureLogging(LogLevel.Warning)
    .build();

  // the same event can arrive twice (the backend delivers at least once); ignore the repeat
  const seen = new Set<string>();
  const order: string[] = [];
  connection.on("changed", (c: CrmChange) => {
    if (seen.has(c.eventId)) return;
    seen.add(c.eventId); order.push(c.eventId);
    if (order.length > 500) seen.delete(order.shift()!);
    o.onChange(c);
  });
  connection.onreconnecting(() => o.onState?.("reconnecting"));
  connection.onreconnected(() => { o.onState?.("connected"); o.onResync?.(); });
  connection.onclose(() => o.onState?.("disconnected"));

  let stopped = false;
  const start = async () => {
    o.onState?.("connecting");
    for (let attempt = 0; !stopped; attempt++) {
      try { await connection.start(); o.onState?.("connected"); return; }
      catch { await new Promise((r) => setTimeout(r, RETRY_MS[Math.min(attempt, RETRY_MS.length - 1)] || 1000)); }
    }
  };
  void start();

  return {
    connection,
    stop: async () => { stopped = true; if (connection.state !== HubConnectionState.Disconnected) await connection.stop(); },
  };
}
